import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data";
import { BaseComponent } from "./base_component";
import { Input, Node } from "rete";
import { categoricalDataSocket } from "../socket_types";
import { SankeyControl, serializeSankeyVariables } from "../controls/sankey";
import { ExposeToggleControl } from "../controls/expose_toggle_control";
import { BooleanTileGrid, CategoricalTileGrid } from "../tile_grid";
import { getMedianCellSize } from "./cell_area_component";
import { maskFromExtentAndShape } from "../bounding_box";
import type { ProjectProperties } from "./index";

export const SANKEY_COMPONENT_NAME = "Sankey diagram"
export const SANKEY_VARIABLES_KEY = "sankeyVariables"

function computeTransitions(gridA: CategoricalTileGrid, gridB: CategoricalTileGrid, mask?: BooleanTileGrid): Map<string, number> {
    const map = new Map<string, number>();
    const cellSize = getMedianCellSize(gridA).area / (1000 ** 2)

    gridA.iterate((x, y, v) => {
        if (mask && mask.get(x, y, gridA.zoom) !== true) return
        if (v !== 255) { // ignore no-data
            const vB = gridB.get(x, y, gridB.zoom);
            if (vB !== 255) {
                const key = `${v}-${vB}`;
                map.set(key, (map.get(key) || 0) + cellSize);
            }
        }
    });

    return map;
}


function prepareSankeyData(inputs: CategoricalTileGrid[], mask?: BooleanTileGrid) {
    const transitionsArray: {
        transitions: Map<string, number>,
        source: { name: string, labels: Map<number, string> },
        target: { name: string, labels: Map<number, string> }
    }[] = []

    for (let i = 0; i < inputs.length - 1; i++) {
        const left = inputs[i]
        const right = inputs[i + 1]
        const transitions = computeTransitions(left, right, mask)
        transitionsArray.push({
            transitions,
            source: { name: left.name ?? `${i.toString()}`, labels: left.labels },
            target: { name: right.name ?? `${(i + 1).toString()}`, labels: right.labels }
        })
    }

    return transitionsArray
}

export class SankeyComponent extends BaseComponent {
    private readonly projectProps: ProjectProperties

    constructor(projectProps: ProjectProperties) {
        super(SANKEY_COMPONENT_NAME)
        this.category = 'Charts'
        this.projectProps = projectProps
    }
    
    async builder(node: Node) {
        if (!("expose" in node.data)) {
            node.data.expose = node.data.showInDetail === true
        }
        node.addInput(new Input('in', 'Inputs', categoricalDataSocket, true))
        if (!node.controls.has("expose")) {
            node.addControl(new ExposeToggleControl(this.editor, "expose"))
        }
        node.addControl(new SankeyControl('sankey'))
    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
        const editorNode = this.editor?.nodes.find(n => n.id === node.id)

        const sankeyControl = editorNode?.controls.get('sankey')

        if(inputs['in'].length < 2) {
            editorNode!.meta.errorMessage = 'At least 2 inputs are required'
            editorNode!.update()
            return
        }

        const mask = this.projectProps.mask ? await maskFromExtentAndShape(this.projectProps) : undefined
        const sankeyData = prepareSankeyData(inputs['in'] as CategoricalTileGrid[], mask);
        editorNode!.data[SANKEY_VARIABLES_KEY] = serializeSankeyVariables(sankeyData)
        // @ts-expect-error: setVariables is not typed on Control
        sankeyControl!.setVariables(sankeyData);
        // @ts-expect-error: setTitle is not typed on Control
        sankeyControl!.setTitle(editorNode!.data.name as string);
        // @ts-expect-error: update is not typed on Control
        sankeyControl!.update();

        editorNode!.update()


    }
}